import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TenantReport } from "./apis/reports_repository";
import logo from "../assets/images/logo.png";

const formatPeriod = (period: string): string => {
    if (period.includes(" to ")) {
        const [start, end] = period.split(" to ");
        return `${formatPeriod(start)} To ${formatPeriod(end)}`;
    }
    const parts = period.split("-");
    if (parts.length >= 2) {
        const year = Number(parts[0]);
        const month = Number(parts[1]) - 1;
        if (!Number.isNaN(year) && !Number.isNaN(month)) {
            const date = new Date(year, month, 1);
            return `${date.toLocaleString("default", { month: "long" })} ${year}`;
        }
    }
    return period;
};

const formatDateDDMMYYYY = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatPercent = (value?: number) => {
    if (value === undefined || Number.isNaN(value)) {
        return "-";
    }
    return `${parseFloat(value.toFixed(1))}%`;
};

const getAutoTableCursor = (doc: jsPDF) => {
    const pluginState = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable;
    return pluginState?.finalY ?? 20;
};

type AggregatedUser = {
    bookings: number;
    completed: number;
    expired: number;
    completedDates: Set<string>;
    lastCompleted?: string;
};

const getLatestDate = (dates: string[]): string | undefined => {
    const parsed = dates
        .map((value) => new Date(value))
        .filter((value) => !Number.isNaN(value.getTime()))
        .sort((a, b) => b.getTime() - a.getTime());
    return parsed.length ? parsed[0].toISOString().split("T")[0] : undefined;
};

const aggregateReports = (reports: TenantReport[], tenant: string): TenantReport => {
    const usersMap = new Map<string, AggregatedUser>();

    reports.forEach((report) => {
        report.users.forEach((user) => {
            const key = user.user;
            const current = usersMap.get(key) ?? { bookings: 0, completed: 0, expired: 0, completedDates: new Set<string>() };
            current.bookings += user.bookings ?? 0;
            current.completed += user.completed ?? 0;
            current.expired += user.expired ?? 0;
            (user.completedDates ?? []).forEach((date) => {
                if (date) current.completedDates.add(date);
            });
            if (user.lastCompleted) {
                const existingDate = current.lastCompleted ? new Date(current.lastCompleted) : undefined;
                const incomingDate = new Date(user.lastCompleted);
                if (!existingDate || incomingDate > existingDate) {
                    current.lastCompleted = user.lastCompleted;
                }
            }
            usersMap.set(key, current);
        });
    });

    const users = Array.from(usersMap.entries()).map(([name, stats]) => {
        const bookings = stats.bookings;
        const completed = stats.completed;
        const expired = stats.expired;
        const completionPercent = bookings ? (completed / bookings) * 100 : 0;
        const completedDates = Array.from(stats.completedDates).sort();
        const lastCompleted = stats.lastCompleted ?? getLatestDate(completedDates);

        return {
            user: name,
            bookings,
            completed,
            expired,
            completionPercent,
            lastCompleted,
            completedDates,
        };
    });

    const totalBookings = users.reduce((sum, u) => sum + u.bookings, 0);
    const totalCompleted = users.reduce((sum, u) => sum + u.completed, 0);
    const totalExpired = users.reduce((sum, u) => sum + u.expired, 0);
    const avgPerUser = users.length ? totalBookings / users.length : 0;

    const periods = reports
        .map((r) => r.period)
        .filter(Boolean)
        .sort();

    const periodLabel =
        periods.length > 1
            ? `${periods[0]} to ${periods[periods.length - 1]}`
            : periods[0] ?? "";

    const generatedAtLatest = getLatestDate(
        reports
            .map((r) => r.generatedAt)
            .filter((v): v is string => Boolean(v))
    );

    const timezone = reports.find((r) => r.timezone)?.timezone;

    return {
        id: `${tenant}_aggregated_${periodLabel || "range"}`,
        tenant,
        period: periodLabel,
        summary: {
            totalBookings,
            completed: totalCompleted,
            expired: totalExpired,
            avgPerUser,
            completionRate: totalBookings ? (totalCompleted / totalBookings) * 100 : undefined,
        },
        users,
        generatedBy: reports.find((r) => r.generatedBy)?.generatedBy,
        generatedAt: generatedAtLatest,
        timezone,
    };
};

const hexToRgb = (hex: string): [number, number, number] => {
    const cleanHex = hex.replace('#', '').trim();
    if (cleanHex.length === 3) {
        const r = parseInt(cleanHex[0] + cleanHex[0], 16);
        const g = parseInt(cleanHex[1] + cleanHex[1], 16);
        const b = parseInt(cleanHex[2] + cleanHex[2], 16);
        return [r, g, b];
    } else if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return [r, g, b];
    }
    return [0, 0, 0];
};

const getThemeColor = (variable: string, fallback: [number, number, number]): [number, number, number] => {
    try {
        const style = getComputedStyle(document.documentElement);
        const value = style.getPropertyValue(variable).trim();
        if (value) return hexToRgb(value);
    } catch (e) {
        // In case of SSR or other issues
    }
    return fallback;
};

const getRangeFromLabel = (label: string) => {
    const parts = label.split("_to_");
    if (parts.length === 2) {
        return { start: parts[0], end: parts[1] };
    }
    return null;
};

const fillMissingPeriods = (data: { period: string, completed: number, expired: number, total: number }[], rangeLabel: string) => {
    const range = getRangeFromLabel(rangeLabel);
    if (!range) return data;

    const filledData = [...data];
    let currentYear = parseInt(range.start.split('-')[0]);
    let currentMonth = parseInt(range.start.split('-')[1]);
    
    const endYear = parseInt(range.end.split('-')[0]);
    const endMonth = parseInt(range.end.split('-')[1]);
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
        const period = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
        const existing = filledData.find(d => d.period === period);
        
        if (!existing) {
            filledData.push({
                period,
                completed: 0,
                expired: 0,
                total: 0
            });
        }
        
        currentMonth++;
        if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
        }
    }
    return filledData.sort((a, b) => a.period.localeCompare(b.period));
};

export const generateTenantReportsPdf = (reports: TenantReport[], tenant: string, rangeLabel: string, generatedByName: string) => {
    const doc = new jsPDF();
    const primaryColor = getThemeColor('--report-primary', [0, 86, 179]);
    const summaryHeaderBg = getThemeColor('--report-header-bg', [235, 245, 255]);
    const greyText = getThemeColor('--report-text-grey', [100, 100, 100]);

    const sources = reports.length > 1 ? [aggregateReports(reports, tenant)] : reports;

    sources.forEach((report, index) => {
        if (index > 0) {
            doc.addPage();
        }

        // --- Header ---
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Tenant booking summary", 14, 20);

        // Logo
        try {
            doc.addImage(logo, "PNG", 160, 10, 35, 25);
        } catch (e) {
            console.warn("Could not add logo to PDF", e);
        }

        // Tenant Info
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`Tenant Name: ${tenant}`, 14, 30);
        doc.text(`Period: ${formatPeriod(report.period)}`, 14, 35);

        let cursorY = 45;

        // --- Summary Section ---
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Summary", 14, cursorY);
        cursorY += 5;

        const totalBookings = report.summary.totalBookings ?? 0;
        const completedCount = report.summary.completed ?? 0;
        const expiredCount = report.summary.expired ?? 0;
        
        const completedPercent = totalBookings ? ((completedCount / totalBookings) * 100).toFixed(1) : "0.0";
        const expiredPercent = totalBookings ? ((expiredCount / totalBookings) * 100).toFixed(1) : "0.0";

        autoTable(doc, {
            startY: cursorY,
            head: [["Total bookings", "Completed", "No Show", "Avg / user"]],
            body: [[
                totalBookings.toString(),
                `${completedCount} (${parseFloat(completedPercent)}%)`,
                `${expiredCount} (${parseFloat(expiredPercent)}%)`,
                `${report.summary.avgPerUser?.toFixed(1) ?? "0"} bookings`
            ]],
            theme: 'grid',
            styles: { 
                cellPadding: 4, 
                fontSize: 10, 
                textColor: [0, 0, 0],
                lineColor: [200, 200, 200],
                lineWidth: 0.1
            },
            headStyles: { 
                fillColor: summaryHeaderBg, 
                textColor: [0, 0, 0], 
                fontStyle: 'bold',
                lineColor: [200, 200, 200]
            },
            bodyStyles: {
                fontStyle: 'bold'
            }
        });

        cursorY = getAutoTableCursor(doc) + 15;

        // --- Chart Section ---
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Bookings breakdown for period", 14, cursorY);

        // Legend (Aligned right, same line as title)
        const legendY = cursorY - 1; 
        const legendRightX = 14 + 182; 
        
        // No Show
        doc.setFillColor(200, 200, 200);
        doc.rect(legendRightX - 15, legendY - 3, 3, 3, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text("No Show", legendRightX - 10, legendY);

        // Completed
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.rect(legendRightX - 40, legendY - 3, 3, 3, 'F');
        doc.text("Completed", legendRightX - 35, legendY);

        cursorY += 10;

        // Monthly Stacked Bar Chart
        const chartHeight = 50;
        const chartWidth = 182; // Full width to match other sections
        const chartX = 14;
        const chartY = cursorY;

        // Draw Axes
        doc.setDrawColor(200, 200, 200);
        doc.line(chartX, chartY, chartX, chartY + chartHeight); // Y Axis
        doc.line(chartX, chartY + chartHeight, chartX + chartWidth, chartY + chartHeight); // X Axis

        // Prepare Chart Data
        // If we have multiple reports (aggregated), use the original 'reports' array for monthly breakdown.
        // If we have a single report, use it (it will be a single bar).
        const chartData = reports.map(r => ({
            period: r.period,
            completed: r.summary.completed ?? 0,
            expired: r.summary.expired ?? 0,
            total: r.summary.totalBookings ?? 0
        })).sort((a, b) => a.period.localeCompare(b.period));

        const filledChartData = fillMissingPeriods(chartData, rangeLabel);

        const maxTotal = Math.max(...filledChartData.map(d => d.total), 1); // Avoid division by zero
        const barCount = filledChartData.length;
        const barWidth = Math.min(30, (chartWidth / barCount) * 0.6); // Max width 30, or 60% of available slot
        const slotWidth = chartWidth / barCount;

        filledChartData.forEach((data, i) => {
            const barX = chartX + (i * slotWidth) + (slotWidth - barWidth) / 2;
            
            if (data.total > 0) {
                // Scale heights relative to the maximum total in the dataset (so bars are proportional to each other)
                // OR scale relative to the chart height (100% = chartHeight)?
                // Usually charts scale to the max value.
                const scaleFactor = chartHeight / maxTotal;
                
                const completedHeight = data.completed * scaleFactor;
                const expiredHeight = data.expired * scaleFactor;

                // Completed Part (Blue) - Bottom
                doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                doc.rect(barX, chartY + chartHeight - completedHeight, barWidth, completedHeight, 'F');
                
                // Label inside bar
                if (completedHeight > 5) {
                    doc.setTextColor(255, 255, 255);
                    doc.setFontSize(8);
                    doc.text(data.completed.toString(), barX + barWidth / 2, chartY + chartHeight - completedHeight / 2, { align: 'center' });
                }

                // Expired Part (Grey) - Top
                doc.setFillColor(200, 200, 200);
                doc.rect(barX, chartY + chartHeight - completedHeight - expiredHeight, barWidth, expiredHeight, 'F');

                 // Label inside bar
                if (expiredHeight > 5) {
                    doc.setTextColor(0, 0, 0);
                    doc.setFontSize(8);
                    doc.text(data.expired.toString(), barX + barWidth / 2, chartY + chartHeight - completedHeight - expiredHeight / 2, { align: 'center' });
                }
            }
            
            // X Axis Label
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(8);
            doc.text(formatPeriod(data.period), barX + barWidth / 2, chartY + chartHeight + 5, { align: 'center', maxWidth: slotWidth });
        });

        cursorY += chartHeight + 15;

        // --- User Performance Section ---
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("User Performance", 14, cursorY);
        cursorY += 10;

        const pageHeight = doc.internal.pageSize.height;

        report.users.forEach((user) => {
            // Check for page break
            if (cursorY > pageHeight - 40) {
                doc.addPage();
                cursorY = 20;
            }

            // User Name Background
            doc.setFillColor(245, 245, 245);
            doc.rect(14, cursorY, 182, 8, 'F'); 
            
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text(user.user, 16, cursorY + 5.5);
            
            cursorY += 12;

            // Stats Row
            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            
            const stats = [
                `Bookings: ${user.bookings ?? 0}`,
                `Completed: ${user.completed ?? 0}`,
                `No Show: ${user.expired ?? 0}`,
                `Completion: ${formatPercent(user.completionPercent ?? (user.bookings ? (user.completed / user.bookings) * 100 : undefined))}`,
                `Last: ${formatDateDDMMYYYY(user.lastCompleted)}`
            ];
            
            let currentX = 16;
            stats.forEach(stat => {
                doc.text(stat, currentX, cursorY);
                currentX += 35; 
            });
            
            cursorY += 6;

            // Completed Dates
            if (user.completedDates && user.completedDates.length > 0) {
                doc.setFont("helvetica", "bold");
                doc.text("Completed dates:", 16, cursorY);
                
                doc.setFont("helvetica", "normal");
                const datesStr = user.completedDates.map(d => formatDateDDMMYYYY(d)).join(", ");
                const splitDates = doc.splitTextToSize(datesStr, 135);
                doc.text(splitDates, 50, cursorY);
                
                cursorY += (splitDates.length * 4) + 4;
            } else {
                 cursorY += 4;
            }
            
            cursorY += 2; // Extra spacing
        });

        // --- Footer ---
        const pageCount = doc.getNumberOfPages();
        const generatedOn = `Report Generated On: ${new Date().toLocaleString("en-AU")}`;
        const by = `Report Generated By: ${generatedByName}`;

        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            
            let footerY = pageHeight - 20;
            
            doc.setTextColor(greyText[0], greyText[1], greyText[2]);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");

            doc.text(by, 14, footerY);
            doc.text(generatedOn, 14, footerY + 4);
            
            // Page Number
            const pageWidth = doc.internal.pageSize.width;
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, footerY + 4, { align: 'right' });
        }
    });

    const sanitizedRange = rangeLabel.replace(/\s+/g, "_");
    const sanitizedTenant = tenant.replace(/\s+/g, "_");
    doc.save(`tenant_${sanitizedTenant}_${sanitizedRange}.pdf`);
};
